import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/ui';
import { Breadcrumbs } from 'components/shared/Breadcrumbs';
import {
  // StarIcon,
  // FlagIcon,
  // Cog6ToothIcon,
  Bars3Icon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  DevicePhoneMobileIcon,
  ArrowPathIcon,
  FlagIcon,
  FireIcon,
  AcademicCapIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useLocation } from 'react-router';
import { getIntegrationProviders } from 'services/integrationService';
import { toast } from 'sonner';
// import { 
//   HeartIcon as HeartSolid,
//   FlagIcon as FlagSolid
// } from '@heroicons/react/24/solid';

const LeadDetailsHeader = ({
  // isFavorite, 
  // setIsFavorite, 
  // isFlaged, 
  // setIsFlaged, 
  showRightPanel,
  setShowRightPanel,
  LeadRightSidebar,
  handleStageChange,
  leadData,
  setIsFollowUpOpen,
  isMobile,
  showMobileSidebar,
  setShowMobileSidebar,
  stageConfirmModal,
  onLeadDataRefresh, // New prop for refreshing lead data
  showStageModal,
  setShowStageModal,
  // Modal setters for LeadRightSidebar
  setShowOwnerModal,
  setShowPriorityModal,
  setShowAnalyticsModal,
  setShowWhatsAppModal,
  setShowEmailModal,
  setShowSMSModal,
  setShowFollowUpModal,
  setShowCallModal,
  setShowInteractionLevelModal,
  // Quick Actions handlers
  onWhatsApp,
  onCall,
  onEmail,
  onFollowUp,
  onSMS,
  // Lead Management handlers
  onChangeStage,
  onSetPriority,
  onSetInteractionLevel,
  entityName = null, // Add entityName prop for breadcrumb navigation
  isModal = false,
  progressData = {},
}) => {
  const isApplicationFinalSubmitted = Boolean(progressData?.IsFinalSubmitted);
  const isFeePaymentDone = Boolean(progressData?.IsFeePaymentDone || progressData?.isFeePaymentDone);

  const showReadOnlyBanner = leadData?.IsStudent || (isApplicationFinalSubmitted && isFeePaymentDone);

  const location = useLocation();
  const isManager = location.pathname.includes("manager");

  const [integrationAvailability, setIntegrationAvailability] = useState({
    whatsapp: false,
    twilio: false,
    smtp: false,
    sendgrid: false,
  });

  const handleIntegrationAction = (isEnabled, label, callback) => {
    if (!isEnabled) {
      toast.info(`${label} integration is disabled. Enable it under Settings → Integrations to use this action.`);
      return;
    }
    callback?.();
  };

  useEffect(() => {
    let isMounted = true;

    const fetchIntegrationProviders = async () => {
      try {
        const response = await getIntegrationProviders();
        const availability = {
          whatsapp: false,
          twilio: false,
          smtp: false,
          sendgrid: false,
        };

        if (response?.success && Array.isArray(response.data)) {
          response.data.forEach((provider) => {
            const normalizedName = String(
              provider?.Name || provider?.name || provider?.Code || provider?.code || "",
            )
              .trim()
              .toLowerCase();
            const isActive = provider?.IsActive ?? provider?.isActive;
            const activeFlag = isActive === true;

            if (normalizedName.includes("whatsapp")) {
              availability.whatsapp = activeFlag;
            }

            if (normalizedName.includes("twilio")) {
              availability.twilio = activeFlag;
            }

            if (normalizedName.includes("sendgrid")) {
              availability.sendgrid = activeFlag;
            }
            if (normalizedName.includes("smtp")) {
              availability.smtp = activeFlag;
            }
          });
        }

        if (isMounted) {
          setIntegrationAvailability(availability);
        }
      } catch (error) {
        console.error("Error loading integration providers:", error);
        if (isMounted) {
          setIntegrationAvailability({
            whatsapp: false,
            twilio: false,
            smtp: false,
            sendgrid: false,
          });
        }
      }
    };

    fetchIntegrationProviders();

    return () => {
      isMounted = false;
    };
  }, []);

  // Determine if showing Lead or Application breadcrumbs
  // Priority: 1. entityName prop, 2. URL path
  const isLeadView = entityName === 'Lead' || (entityName === null && isManager);

  // Check for valid email
  const leadEmail = leadData?.email || leadData?.Email || leadData?.EmailAddress;
  const hasValidEmail = leadEmail && leadEmail !== "N/A" && leadEmail.trim() !== "";

  const breadcrumbItems = [
    { title: isLeadView ? 'All Leads' : 'All Applications', path: isLeadView ? '/leads/manager' : '/application/listing' },
    { title: `${isLeadView ? 'Lead Details' : 'Applicant Details'} - ${leadData?.name}${leadData?.FatherName ? ` ${leadData?.gender?.trim()?.toLowerCase() === 'male' ? 'S/O' : leadData?.gender?.trim()?.toLowerCase() === 'female' ? 'D/O' : 'C/O'} ${leadData.FatherName}` : ''}` }
  ];

  return (
    <>
      {showReadOnlyBanner && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-purple-100 bg-purple-50/50 px-3 py-2 text-[13px] text-purple-700 dark:border-purple-800/30 dark:bg-purple-900/20 dark:text-purple-300">
          <InformationCircleIcon className="h-4 w-4 shrink-0 text-purple-500 dark:text-purple-400" />
          <p className="flex items-center gap-1.5">
            <span className="font-bold">Read-Only:</span> 
            <span>{leadData?.IsStudent ? 'Converted Student record.' : 'Application Final Submitted & Payment Done.'} No modifications or communication triggers are allowed.</span>
          </p>
        </div>
      )}
      <div className={`flex items-center justify-between mb-1 ${isMobile ? 'flex-wrap gap-1' : ''}`}>
      {/* Mobile menu button and breadcrumbs container */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {isMobile && (
          <Button
            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
            variant="flat"
            color="neutral"
            className="p-2 shrink-0"
          >
            <Bars3Icon className="w-5 h-5" />
          </Button>
        )}
        <div className="min-w-0 flex-1 flex items-center gap-2">
          {!isModal && <Breadcrumbs items={breadcrumbItems} className={isMobile ? 'text-sm' : ''} />}
          {leadData?.IsInternationalLead && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium whitespace-nowrap">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
              </svg>
              International
            </span>
          )}
          {leadData?.IsStudent && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium whitespace-nowrap" title="Student">
              <AcademicCapIcon className="w-4 h-4" />
              Student
            </span>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <div className={`flex items-center gap-1 ${isMobile ? 'mt-1 w-full justify-end' : ''}`}>
        {/* <Button
          onClick={() => setIsFavorite(!isFavorite)}
          variant="flat"
          color="neutral"
          className={`p-2 ${isMobile ? 'text-xs' : ''}`}
        >
          {isFavorite ? <HeartSolid className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" /> : <StarIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
        </Button>
        <Button
          onClick={() => setIsFlaged(!isFlaged)}
          variant="flat"
          color="neutral"
          className={`p-2 ${isMobile ? 'text-xs' : ''}`}
        >
          {isFlaged ? <FlagSolid className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" /> : <FlagIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
        </Button> */}

        {/* Quick Actions - same icon+text layout as sidebar */}
        {!isMobile && (
          <div className="flex items-center gap-1 mr-1">
            {onWhatsApp && (
              <Button
                variant="soft"
                color="success"
                onClick={onWhatsApp}
                disabled={leadData?.IsStudent}
                className={`flex h-auto min-w-[60px] flex-col items-center p-1 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <ChatBubbleLeftRightIcon className="mb-0.5 h-4 w-4" />
                <span className="text-xs leading-tight">WhatsApp</span>
              </Button>
            )}
            {onCall && (
              <Button
                variant="soft"
                color="primary"
                onClick={() => handleIntegrationAction(integrationAvailability.twilio, "Twilio", onCall)}
                disabled={leadData?.IsStudent}
                className={`flex h-auto min-w-[60px] flex-col items-center p-1 ${integrationAvailability.twilio && !leadData?.IsStudent ? "" : "cursor-not-allowed opacity-60"
                  }`}
              >
                <PhoneIcon className="mb-0.5 h-4 w-4" />
                <span className="text-xs leading-tight">Call</span>
              </Button>
            )}
            {onEmail && (
              <Button
                variant="soft"
                color="info"
                onClick={() => {
                  if (!hasValidEmail) {
                    toast.error("Email address not available for this lead");
                    return;
                  }
                  onEmail();
                }}
                disabled={leadData?.IsStudent}
                title={!hasValidEmail ? "Email address not available for this lead" : leadData?.IsStudent ? "Cannot send email to student" : "Send email"}
                className={`flex h-auto min-w-[60px] flex-col items-center p-1 ${(!hasValidEmail || leadData?.IsStudent) ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <EnvelopeIcon className="mb-0.5 h-4 w-4" />
                <span className="text-xs leading-tight">Email</span>
              </Button>
            )}
            {onFollowUp && (
              <Button
                variant="soft"
                color="warning"
                onClick={onFollowUp}
                disabled={leadData?.IsStudent}
                className={`flex h-auto min-w-[60px] flex-col items-center p-1 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <CalendarDaysIcon className="mb-0.5 h-4 w-4" />
                <span className="text-xs leading-tight">Follow-up</span>
              </Button>
            )}
            {onSMS && (
              <Button
                variant="soft"
                color="neutral"
                onClick={() => handleIntegrationAction(integrationAvailability.twilio, "Twilio", onSMS)}
                disabled={leadData?.IsStudent}
                className={`flex h-auto min-w-[60px] flex-col items-center p-1 ${integrationAvailability.twilio && !leadData?.IsStudent ? "" : "cursor-not-allowed opacity-60"
                  }`}
              >
                <DevicePhoneMobileIcon className="mb-0.5 h-4 w-4" />
                <span className="text-xs leading-tight">SMS</span>
              </Button>
            )}
          </div>
        )}

        {/* {LeadRightSidebar && (
          <LeadRightSidebar 
            leadData={leadData}
            setIsFollowUpOpen={setIsFollowUpOpen}
            showRightPanel={showRightPanel}
            setShowRightPanel={setShowRightPanel}
            isMobile={isMobile}
            handleStageChange={handleStageChange}
            stageConfirmModal={stageConfirmModal}
            onLeadDataRefresh={onLeadDataRefresh}
            showStageModal={showStageModal}
            setShowStageModal={setShowStageModal}
            // Pass modal setters
            setShowOwnerModal={setShowOwnerModal}
            setShowPriorityModal={setShowPriorityModal}
            setShowAnalyticsModal={setShowAnalyticsModal}
            setShowWhatsAppModal={setShowWhatsAppModal}
            setShowEmailModal={setShowEmailModal}
            setShowSMSModal={setShowSMSModal}
            setShowFollowUpModal={setShowFollowUpModal}
            setShowCallModal={setShowCallModal}
            setShowInteractionLevelModal={setShowInteractionLevelModal}
          />
        )} */}
      </div>
    </div>
  </>
);
};

LeadDetailsHeader.propTypes = {
  isFavorite: PropTypes.bool,
  setIsFavorite: PropTypes.func,
  isFlaged: PropTypes.bool,
  setIsFlaged: PropTypes.func,
  showRightPanel: PropTypes.bool,
  setShowRightPanel: PropTypes.func,
  LeadRightSidebar: PropTypes.elementType,
  handleStageChange: PropTypes.func,
  leadData: PropTypes.object,
  setIsFollowUpOpen: PropTypes.func,
  isMobile: PropTypes.bool,
  showMobileSidebar: PropTypes.bool,
  setShowMobileSidebar: PropTypes.func,
  stageConfirmModal: PropTypes.object,
  onLeadDataRefresh: PropTypes.func,
  showStageModal: PropTypes.bool,
  setShowStageModal: PropTypes.func,
  // Modal setters
  setShowOwnerModal: PropTypes.func,
  setShowPriorityModal: PropTypes.func,
  setShowAnalyticsModal: PropTypes.func,
  setShowWhatsAppModal: PropTypes.func,
  setShowEmailModal: PropTypes.func,
  setShowSMSModal: PropTypes.func,
  setShowFollowUpModal: PropTypes.func,
  setShowCallModal: PropTypes.func,
  entityName: PropTypes.string,
};

export default LeadDetailsHeader;
